/**
 * Where the voice orb sits, and moving it.
 *
 * Owns one concern: the orb's place on screen. By default it is in the bottom
 * right corner; a person can drag it anywhere (mouse, finger or pen) or nudge it
 * with Shift + arrow keys, and it stays there across reloads.
 *
 * Choices worth knowing:
 * - A press only becomes a drag after it moves ``DRAG_PX``. Below that it is a
 *   click and starts or ends a call, so a slightly shaky tap never moves the
 *   orb and a drag never starts a call (``consumeClick`` swallows the click a
 *   drag ends with).
 * - The position is stored as the orb's centre in *fractions of the viewport*,
 *   so it lands in the same place on a resized window or a rotated phone, and
 *   is clamped so it can never be off screen.
 * - ``placement`` says which half of the screen the orb is in, so the captions,
 *   dock and errors open toward the free side instead of off the edge.
 * - Storage may be blocked (private windows): then the position lasts the tab.
 */
import * as React from "react";

const KEY = "omni.orb.position";
export const ORB_SIZE = 64;
const EDGE = 8; // space kept between the orb and the screen edge
const CORNER = 24; // the default distance from the bottom right corner (was bottom-6 right-6)
const DRAG_PX = 6;
const NUDGE_PX = 16;

type Fraction = { fx: number; fy: number };

function load(): Fraction | null {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as Partial<Fraction>;
    return typeof p.fx === "number" && typeof p.fy === "number" && p.fx >= 0 && p.fx <= 1 && p.fy >= 0 && p.fy <= 1
      ? { fx: p.fx, fy: p.fy }
      : null;
  } catch {
    return null;
  }
}

function save(p: Fraction | null) {
  try {
    if (p) window.localStorage.setItem(KEY, JSON.stringify(p));
    else window.localStorage.removeItem(KEY);
  } catch {
    /* private window: the position lasts until the tab closes */
  }
}

export interface OrbPlacement {
  /** The orb is in the right half: extras open to its left, text aligns right. */
  right: boolean;
  /** The orb is in the lower half: extras open above it. */
  bottom: boolean;
}

export function useOrbPosition() {
  const [fraction, setFraction] = React.useState<Fraction | null>(load);
  const [viewport, setViewport] = React.useState(() => ({ w: window.innerWidth, h: window.innerHeight }));
  const [dragging, setDragging] = React.useState(false);
  const gesture = React.useRef<{ id: number; sx: number; sy: number; ox: number; oy: number; moved: boolean } | null>(null);
  const swallowClick = React.useRef(false);

  React.useEffect(() => {
    const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const half = ORB_SIZE / 2;
  const clamp = React.useCallback(
    (x: number, y: number) => ({
      x: Math.min(Math.max(x, half + EDGE), Math.max(half + EDGE, viewport.w - half - EDGE)),
      y: Math.min(Math.max(y, half + EDGE), Math.max(half + EDGE, viewport.h - half - EDGE)),
    }),
    [half, viewport.w, viewport.h],
  );

  const centre = clamp(
    fraction ? fraction.fx * viewport.w : viewport.w - CORNER - half,
    fraction ? fraction.fy * viewport.h : viewport.h - CORNER - half,
  );
  const moved = fraction !== null;

  const place = React.useCallback(
    (x: number, y: number, persist: boolean) => {
      const c = clamp(x, y);
      const next = { fx: c.x / viewport.w, fy: c.y / viewport.h };
      setFraction(next);
      if (persist) save(next);
    },
    [clamp, viewport.w, viewport.h],
  );

  const onPointerDown = (e: React.PointerEvent<HTMLElement>) => {
    if (e.button !== 0) return;
    gesture.current = { id: e.pointerId, sx: e.clientX, sy: e.clientY, ox: centre.x, oy: centre.y, moved: false };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent<HTMLElement>) => {
    const g = gesture.current;
    if (!g || g.id !== e.pointerId) return;
    const dx = e.clientX - g.sx;
    const dy = e.clientY - g.sy;
    if (!g.moved && Math.hypot(dx, dy) < DRAG_PX) return;
    if (!g.moved) {
      g.moved = true;
      setDragging(true);
    }
    place(g.ox + dx, g.oy + dy, false);
  };
  const endGesture = (e: React.PointerEvent<HTMLElement>) => {
    const g = gesture.current;
    if (!g || g.id !== e.pointerId) return;
    gesture.current = null;
    if (g.moved) {
      // The click that follows a drag is not a request to start a call.
      swallowClick.current = true;
      window.setTimeout(() => (swallowClick.current = false), 0);
      place(g.ox + (e.clientX - g.sx), g.oy + (e.clientY - g.sy), true);
      setDragging(false);
    }
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (!e.shiftKey) return;
    const step: Record<string, [number, number]> = {
      ArrowLeft: [-NUDGE_PX, 0],
      ArrowRight: [NUDGE_PX, 0],
      ArrowUp: [0, -NUDGE_PX],
      ArrowDown: [0, NUDGE_PX],
    };
    const d = step[e.key];
    if (!d) return;
    e.preventDefault();
    place(centre.x + d[0], centre.y + d[1], true);
  };

  const reset = () => {
    setFraction(null);
    save(null);
  };

  const placement: OrbPlacement = { right: centre.x > viewport.w / 2, bottom: centre.y > viewport.h / 2 };
  // Anchored by the screen edge the orb is nearest, so the orb stays put while
  // captions appear and disappear on the other side of it.
  const style: React.CSSProperties = {
    ...(placement.right ? { right: viewport.w - centre.x - half } : { left: centre.x - half }),
    ...(placement.bottom ? { bottom: viewport.h - centre.y - half } : { top: centre.y - half }),
  };
  if (!moved) {
    // In its corner it also clears a phone's home bar and notch; once a person has put it somewhere, that is where it stays.
    style.right = `max(${CORNER}px, env(safe-area-inset-right, 0px))`;
    style.bottom = `max(${CORNER}px, env(safe-area-inset-bottom, 0px))`;
    delete style.left;
    delete style.top;
  }

  return {
    style,
    placement,
    dragging,
    moved,
    reset,
    /** True once, for the click a drag ends with. */
    consumeClick: () => {
      const was = swallowClick.current;
      swallowClick.current = false;
      return was;
    },
    handlers: { onPointerDown, onPointerMove, onPointerUp: endGesture, onPointerCancel: endGesture, onKeyDown },
  };
}
